/**
 * Column definition generators for TanStack Table columns
 */

import type { SchemaColumn, SchemaField } from '@core-engine/schema/types.js'
import { buildColumnSize } from '@next-adapter/generators/table-column-size.js'

// ============================================================================
// Column Helpers
// ============================================================================

export function isSortableColumn(column: SchemaColumn): boolean {
  return column.sortable === true
}

export function buildColumnMeta(column: SchemaColumn, extra?: Record<string, string>): string {
  const props: Record<string, string> = { ...extra }
  if (column.align && column.align !== 'left') {
    props.align = column.align
  }
  const entries = Object.entries(props)
  if (entries.length === 0) return ''
  const inner = entries.map(([k, v]) => `${k}: '${v}'`).join(', ')
  return `meta: { ${inner} },\n    `
}

export function generateTitleFallback(fields: SchemaField[]): string {
  const fieldNames = new Set(fields.map((f) => f.name))
  const fallbacks: string[] = []
  if (fieldNames.has('title')) fallbacks.push('row.original.title')
  if (fieldNames.has('name')) fallbacks.push('row.original.name')
  fallbacks.push('row.original.id')
  fallbacks.push("''")
  return fallbacks.join(' || ')
}

function quoteStringLiteral(value: string): string {
  const escaped = value
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n')

  return `'${escaped}'`
}

function getBooleanColumnLabels(
  column: SchemaColumn,
  fields: SchemaField[]
): { trueLabel: string; falseLabel: string } {
  const field = fields.find((item) => item.name === column.accessorKey)
  const baseLabel = field?.label || column.header
  const defaultLabels =
    column.accessorKey === 'published'
      ? { trueLabel: 'Published', falseLabel: 'Draft' }
      : { trueLabel: baseLabel, falseLabel: `Not ${baseLabel}` }

  return {
    trueLabel: quoteStringLiteral(column.trueLabel ?? defaultLabels.trueLabel),
    falseLabel: quoteStringLiteral(column.falseLabel ?? defaultLabels.falseLabel)
  }
}

function getAuthorshipUserAccessor(accessorKey: string): 'createdByUser' | 'updatedByUser' | null {
  if (accessorKey === 'createdBy') return 'createdByUser'
  if (accessorKey === 'updatedBy') return 'updatedByUser'
  return null
}

function generateAuthorshipValue(userAccessor: 'createdByUser' | 'updatedByUser'): string {
  return `const actor = row.original.${userAccessor}
      const label = actor?.name || actor?.email`
}

function fieldHasMediaCompanion(column: SchemaColumn, fields: SchemaField[]): boolean {
  const field = fields.find((item) => item.name === column.accessorKey)
  return field?.type === 'image' || field?.type === 'media' || field?.type === 'video'
}

function fieldIsSelect(column: SchemaColumn, fields: SchemaField[]): boolean {
  return fields.find((item) => item.name === column.accessorKey)?.type === 'select'
}

function fieldIsRelationship(column: SchemaColumn, fields: SchemaField[]): boolean {
  return fields.find((item) => item.name === column.accessorKey)?.type === 'relationship'
}

function generateSelectLabel(valueExpression: string): string {
  return `const label = Array.isArray(${valueExpression})
        ? ${valueExpression}
            .map((option) =>
              typeof option === 'object' && option !== null && 'label' in option
                ? String(option.label)
                : String(option)
            )
            .join(', ')
        : ${valueExpression} === null || ${valueExpression} === undefined
          ? null
          : String(${valueExpression})`
}

function generateRelationshipLabel(valueExpression: string): string {
  return `const label = (() => {
        const toRelationshipLabel = (item: unknown): string | null => {
          if (item && typeof item === 'object') {
            const display =
              ('name' in item && item.name) ||
              ('title' in item && item.title) ||
              ('label' in item && item.label) ||
              ('email' in item && item.email) ||
              ('id' in item && item.id)
            return display === null || display === undefined || display === ''
              ? null
              : String(display)
          }
          return item === null || item === undefined ? null : String(item)
        }

        return Array.isArray(${valueExpression})
          ? ${valueExpression}.map(toRelationshipLabel).filter(Boolean).join(', ')
          : toRelationshipLabel(${valueExpression})
      })()`
}

function buildImageUrlExpression(
  column: SchemaColumn,
  fields: SchemaField[],
  fallbackExpression: string
): string {
  const fallback = `(${fallbackExpression} as string | null | undefined)`

  if (!fieldHasMediaCompanion(column, fields)) {
    return fallback
  }

  return `row.original.${column.accessorKey}Media?.url ?? ${fallback}`
}

function generateImageElement(column: SchemaColumn): string {
  return `<Avatar className="size-10 overflow-hidden border">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={title ? \`${column.header} image for \${title}\` : '${column.header} image'}
              fill
              sizes="40px"
              className="rounded-full object-cover"
              unoptimized
            />
          ) : (
            <AvatarFallback>
              {String(title || imageUrl).substring(0, 2).toUpperCase()}
            </AvatarFallback>
          )}
        </Avatar>`
}

function generateImageCellMedia(
  column: SchemaColumn,
  fields: SchemaField[],
  valueExpression: string,
  options: { selectable?: boolean } = {}
): string {
  const imageUrlExpression = buildImageUrlExpression(column, fields, valueExpression)
  const titleFallback = generateTitleFallback(fields)
  const imageElement = generateImageElement(column)

  if (options.selectable) {
    return `const imageUrl = ${imageUrlExpression} ?? ''
      const title = ${titleFallback}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          ${imageElement.split('\n').join('\n          ')}
        </div>
      )`
  }

  return `const imageUrl = ${imageUrlExpression} ?? ''
      const title = ${titleFallback}
      return (
        ${imageElement}
      )`
}

// ============================================================================
// Cell Renderers
// ============================================================================

function buildSortableHeaderButtonJsx(
  column: SchemaColumn,
  options: { className: string } = { className: 'w-full' }
): string {
  return `<Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="${options.className} min-w-0 justify-start overflow-hidden px-0! border-none rounded-none bg-transparent! scale-100! group"
          aria-label={\`Sort by ${column.header}, \${sortLabel}\`}
          aria-sort={sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : 'none'}
        >
          <span className="truncate">${column.header}</span>
          <SortIndicator direction={sortDirection} />
        </Button>`
}

function buildSortLabelExpr(): string {
  return `const sortDirection = column.getIsSorted()
      const sortLabel = sortDirection === 'asc'
        ? 'sorted ascending'
        : sortDirection === 'desc'
        ? 'sorted descending'
        : 'not sorted'
`
}

export function generateColumnDef(column: SchemaColumn, fields: SchemaField[]): string {
  const sortable = isSortableColumn(column)
  const headerDef = sortable
    ? `header: ({ column }) => {
      ${buildSortLabelExpr()}
      return (
        ${buildSortableHeaderButtonJsx(column, { className: 'w-full' })}
      )
    }`
    : `header: '${column.header}'`

  let cellDef = ''
  const authorshipUserAccessor = getAuthorshipUserAccessor(column.accessorKey)
  const isSelectColumn = fieldIsSelect(column, fields)
  const isRelationshipColumn = fieldIsRelationship(column, fields)

  if (authorshipUserAccessor) {
    cellDef = `cell: ({ row }) => {
      ${generateAuthorshipValue(authorshipUserAccessor)}
      return label ? (
        <div className="text-sm font-medium" title={actor?.email}>
          {label}
        </div>
      ) : (
        <div className="text-muted-foreground">Unavailable</div>
      )
    }`
  } else
    switch (column.type) {
      case 'image':
      case 'avatar':
        cellDef = `cell: ({ row }) => {
      ${generateImageCellMedia(column, fields, `row.getValue('${column.accessorKey}')`)}
    }`
        break
      case 'badge':
        cellDef = isSelectColumn
          ? `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}')
      ${generateSelectLabel('value')}
      return (
        <Badge variant="outline">
          {label || 'Unavailable'}
        </Badge>
      )
    }`
          : isRelationshipColumn
            ? `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}')
      ${generateRelationshipLabel('value')}
      return (
        <Badge variant="outline">
          {label || 'Unavailable'}
        </Badge>
      )
    }`
            : `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}')
      return (
        <Badge variant="outline">
          {value === null || value === undefined ? 'Unavailable' : String(value)}
        </Badge>
      )
    }`
        break
      case 'date':
        cellDef = `cell: ({ row }) => {
      const date = new Date(row.getValue('${column.accessorKey}'))
      return (
        <div className="text-sm">
          {date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
        </div>
      )
    }`
        break
      case 'email':
        cellDef = `cell: ({ row }) => {
      const email = row.getValue('${column.accessorKey}') as string
      return <div className="text-sm font-medium">{email}</div>
    }`
        break
      case 'number':
        if (column.format === 'currency') {
          cellDef = `cell: ({ row }) => {
      const amount = row.getValue('${column.accessorKey}') as number
      return (
        <div className="text-sm font-medium">
          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)}
        </div>
      )
    }`
        } else {
          cellDef = `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}') as number
      return <div className="text-sm">{value}</div>
    }`
        }
        break
      case 'boolean':
        {
          const { trueLabel, falseLabel } = getBooleanColumnLabels(column, fields)
          cellDef = `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}') as boolean
      return (
        <Badge variant={value ? 'outline' : 'secondary'}>
          {value ? ${trueLabel} : ${falseLabel}}
        </Badge>
      )
    }`
        }
        break
      case 'link':
        cellDef = `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}') as string
      if (!value) return <div className="text-muted-foreground">\u2014</div>
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          title={value}
        >
          {truncateText(value, 50)}
        </a>
      )
    }`
        break
      case 'custom':
        if (column.component) {
          cellDef = `cell: ({ row }) => {
      return <${column.component} data={row.original} />
    }`
        } else {
          cellDef = isSelectColumn
            ? `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}')
      ${generateSelectLabel('value')}
      return <div>{label || 'Unavailable'}</div>
    }`
            : isRelationshipColumn
              ? `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}')
      ${generateRelationshipLabel('value')}
      return <div>{label || 'Unavailable'}</div>
    }`
              : `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}')
      return <div>{value === null || value === undefined ? 'Unavailable' : String(value)}</div>
    }`
        }
        break
      default:
        cellDef = isSelectColumn
          ? `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}')
      ${generateSelectLabel('value')}
      return <div>{label || 'Unavailable'}</div>
    }`
          : isRelationshipColumn
            ? `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}')
      ${generateRelationshipLabel('value')}
      return <div>{label || 'Unavailable'}</div>
    }`
            : `cell: ({ row }) => {
      const value = row.getValue('${column.accessorKey}')
      return <div>{value === null || value === undefined ? 'Unavailable' : String(value)}</div>
    }`
        break
    }

  const meta = buildColumnMeta(column)

  return `  {
    accessorKey: '${column.accessorKey}',
    ${buildColumnSize(column.size)}${meta}${headerDef},
    ${cellDef}
  }`
}

export function generateFirstColumnDef(
  column: SchemaColumn,
  fields: SchemaField[],
  options: { selectable?: boolean } = {}
): string {
  if (options.selectable === false) {
    return generateColumnDef(column, fields)
  }

  const sortable = isSortableColumn(column)
  const headerDef = sortable
    ? `header: ({ table, column }) => {
      ${buildSortLabelExpr()}
      return (
        <div className="flex min-w-0 items-center gap-4 pl-1">
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
          ${buildSortableHeaderButtonJsx(column, { className: 'flex-1' })}
        </div>
      )
    }`
    : `header: ({ table }) => (
      <div className="flex items-center gap-4 pl-1">
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
        <span>${column.header}</span>
      </div>
    )`

  let cellDef = ''
  const authorshipUserAccessor = getAuthorshipUserAccessor(column.accessorKey)
  const isSelectColumn = fieldIsSelect(column, fields)
  const isRelationshipColumn = fieldIsRelationship(column, fields)

  if (authorshipUserAccessor) {
    cellDef = `cell: ({ row }) => {
      ${generateAuthorshipValue(authorshipUserAccessor)}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          {label ? (
            <span className="text-sm font-medium truncate max-w-[300px]" title={actor?.email}>
              {label}
            </span>
          ) : (
            <span className="text-muted-foreground">Unavailable</span>
          )}
        </div>
      )
    }`
  } else
    switch (column.type) {
      case 'image':
      case 'avatar':
        cellDef = `cell: ({ row }) => {
      ${generateImageCellMedia(column, fields, `row.original.${column.accessorKey}`, {
        selectable: true
      })}
    }`
        break
      case 'badge':
        cellDef = isSelectColumn
          ? `cell: ({ row }) => {
      const value = row.original.${column.accessorKey}
      ${generateSelectLabel('value')}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <Badge variant="outline">
            {label || 'Unavailable'}
          </Badge>
        </div>
      )
    }`
          : isRelationshipColumn
            ? `cell: ({ row }) => {
      const value = row.original.${column.accessorKey}
      ${generateRelationshipLabel('value')}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <Badge variant="outline">
            {label || 'Unavailable'}
          </Badge>
        </div>
      )
    }`
            : `cell: ({ row }) => {
      const value = row.original.${column.accessorKey}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <Badge variant="outline">
            {value === null || value === undefined ? 'Unavailable' : String(value)}
          </Badge>
        </div>
      )
    }`
        break
      case 'date':
        cellDef = `cell: ({ row }) => {
      const date = new Date(row.original.${column.accessorKey})
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <div className="text-sm">
            {date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </div>
        </div>
      )
    }`
        break
      case 'email':
        cellDef = `cell: ({ row }) => {
      const email = row.original.${column.accessorKey} as string
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="text-sm font-medium truncate max-w-[300px]">{email}</span>
        </div>
      )
    }`
        break
      case 'number':
        if (column.format === 'currency') {
          cellDef = `cell: ({ row }) => {
      const amount = row.original.${column.accessorKey} as number
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <div className="text-sm font-medium">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD'
            }).format(amount)}
          </div>
        </div>
      )
    }`
        } else {
          cellDef = `cell: ({ row }) => {
      const value = row.original.${column.accessorKey} as number
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <div className="text-sm">{value}</div>
        </div>
      )
    }`
        }
        break
      case 'boolean':
        {
          const { trueLabel, falseLabel } = getBooleanColumnLabels(column, fields)
          cellDef = `cell: ({ row }) => {
      const value = row.original.${column.accessorKey} as boolean
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <Badge variant={value ? 'outline' : 'secondary'}>
            {value ? ${trueLabel} : ${falseLabel}}
          </Badge>
        </div>
      )
    }`
        }
        break
      case 'custom':
        if (column.component) {
          cellDef = `cell: ({ row }) => {
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <${column.component} data={row.original} />
        </div>
      )
    }`
        } else {
          cellDef = isSelectColumn
            ? `cell: ({ row }) => {
      const value = row.original.${column.accessorKey}
      ${generateSelectLabel('value')}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="truncate max-w-[300px]">
            {label || 'Unavailable'}
          </span>
        </div>
      )
    }`
            : isRelationshipColumn
              ? `cell: ({ row }) => {
      const value = row.original.${column.accessorKey}
      ${generateRelationshipLabel('value')}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="truncate max-w-[300px]">
            {label || 'Unavailable'}
          </span>
        </div>
      )
    }`
              : `cell: ({ row }) => {
      const value = row.original.${column.accessorKey}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="truncate max-w-[300px]">
            {value === null || value === undefined ? 'Unavailable' : String(value)}
          </span>
        </div>
      )
    }`
        }
        break
      case 'link':
        cellDef = `cell: ({ row }) => {
      const value = row.original.${column.accessorKey} as string
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          {value ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300 truncate block max-w-[250px]"
              title={value}
              onClick={(e) => e.stopPropagation()}
            >
              {truncateText(value, 50)}
            </a>
          ) : (
            <span className="text-muted-foreground">\u2014</span>
          )}
        </div>
      )
    }`
        break
      default:
        cellDef = isSelectColumn
          ? `cell: ({ row }) => {
      const value = row.original.${column.accessorKey}
      ${generateSelectLabel('value')}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="truncate max-w-[300px]">
            {label || 'Unavailable'}
          </span>
        </div>
      )
    }`
          : isRelationshipColumn
            ? `cell: ({ row }) => {
      const value = row.original.${column.accessorKey}
      ${generateRelationshipLabel('value')}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="truncate max-w-[300px]">
            {label || 'Unavailable'}
          </span>
        </div>
      )
    }`
            : `cell: ({ row }) => {
      const value = row.original.${column.accessorKey}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="truncate max-w-[300px]">
            {value === null || value === undefined ? 'Unavailable' : String(value)}
          </span>
        </div>
      )
    }`
        break
    }

  const meta = buildColumnMeta(column)

  return `  {
    accessorKey: '${column.accessorKey}',
    ${buildColumnSize(column.size)}${meta}${headerDef},
    ${cellDef},
    enableSorting: ${sortable ? 'true' : 'false'},
    enableHiding: false
  }`
}
